import React, { useEffect, useState } from "react";
import { MdVisibility, MdEdit, MdDelete, MdCheckCircle, MdWarning, MdApartment } from "react-icons/md";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import SearchForm from "../../utils/SearchForm";
import { ToastContainer, toast } from "react-toastify";
import AxiosInstance from "../../utils/AxiosInstance";

const API = process.env.REACT_APP_API_URL;
const typeLabels = { Obuasi_Campus: "Obuasi Campus" };
const typeTones = { Hostel: "green", Homestel: "teal", Obuasi_Campus: "amber" };

export default function Residences(props) {
	const navigate = useNavigate();
	const [hostels, setHostels] = useState([]);
	const [all, setAll] = useState([]);
	const [pageCount, setPageCount] = useState(0);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [limit] = useState(35);
	const [isLoading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [reload, setReload] = useState(0);
	const [toDelete, setToDelete] = useState(null);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const controller = new AbortController();
		const fetchHostels = async () => {
			setLoading(true);
			setLoadError(false);
			try {
				const res = await AxiosInstance({
					method: "get",
					url: `/api/v1/residences?page=${page}&limit=${limit}`,
					signal: controller.signal,
				});
				setPageCount(Math.ceil(res.data.total / limit)); // set pageCount
				setHostels(res.data.data || []);
				setTotal(res.data.total || 0);
				setAll(res.data.all || []);
			} catch (err) {
				if (controller.signal.aborted) return;
				console.log(err);
				setLoadError(true);
			} finally {
				if (!controller.signal.aborted) setLoading(false);
			}
		};
		fetchHostels();
		return () => controller.abort();
	}, [page, limit, reload]);

	const handlePageClick = (p) => {
		setPage(p.selected + 1);
	};

	const handleDelete = async () => {
		if (!toDelete) return;
		setIsDeleting(true);
		try {
			const res = await AxiosInstance({
				method: "delete",
				url: `/api/v1/residences/${toDelete._id}`,
			});
			if (res?.data?.status === "success" || res?.status === 204) {
				toast.success(res?.data?.message || `${toDelete.name} deleted`, { position: "top-center" });
				setToDelete(null);
				setReload((r) => r + 1);
			} else {
				toast.error(res?.data?.message || "Couldn't delete the residence", { position: "top-center" });
			}
		} catch (err) {
			toast.error(err?.response?.data?.message || "Couldn't delete the residence", {
				position: "top-center",
			});
		} finally {
			setIsDeleting(false);
		}
	};

	const from = total === 0 ? 0 : (page - 1) * limit + 1;
	const to = Math.min(page * limit, total);

	return (
		<div className="ad-page">
			<ToastContainer style={{ marginTop: "60px" }} />

			<section className="ad-card at-toolbar">
				<div className="at-toolbar-text">
					<h2 className="at-title">
						All Residences <span className="ad-pill">{total} total</span>
					</h2>
					<p className="ad-muted">
						Page {page} of {pageCount || 1}
					</p>
				</div>
				<SearchForm data={all} type="Residence" />
			</section>

			<section className="ad-card at-card">
				<div className="table-container at-scroll">
					<table className="at-table">
						<thead>
							<tr>
								<th>Residence</th>
								<th>Type</th>
								<th>Location</th>
								<th>Zone / Constituency</th>
								<th>Digital Address</th>
								<th>Class</th>
								<th>Status</th>
								<th className="at-actions-col">Actions</th>
							</tr>
						</thead>
						<tbody>
							{isLoading &&
								Array.from({ length: 6 }).map((_, i) => (
									<tr key={`sk-${i}`} className="at-skeleton">
										{Array.from({ length: 8 }).map((__, j) => (
											<td key={j}>
												<span />
											</td>
										))}
									</tr>
								))}

							{!isLoading && loadError && (
								<tr>
									<td colSpan={8} className="at-empty">
										<MdWarning /> Couldn&apos;t load residences.{" "}
										<button className="at-link" onClick={() => setReload((r) => r + 1)}>
											Try again
										</button>
									</td>
								</tr>
							)}

							{!isLoading && !loadError && hostels.length === 0 && (
								<tr>
									<td colSpan={8} className="at-empty">
										<MdApartment /> No residences yet.
									</td>
								</tr>
							)}

							{!isLoading &&
								!loadError &&
								hostels.map((item) => (
									<tr key={item._id}>
										<td>
											<div className="at-name">
												<span className="at-thumb">
													{item.coverImage ? (
														<img
															src={`${API}/api/images/${item.slug}/${item.coverImage}`}
															alt=""
															loading="lazy"
														/>
													) : (
														<MdApartment />
													)}
												</span>
												<span className="at-name-text">
													<button className="at-name-link" onClick={() => navigate(`details/${item.slug}`)}>
														{item?.name}
													</button>
													<small>#{item._id.slice(20, 24)}</small>
												</span>
											</div>
										</td>
										<td>
											<span className={`at-badge at-tone-${typeTones[item.residenceType] || "grey"}`}>
												{typeLabels[item.residenceType] || item.residenceType || "N/A"}
											</span>
										</td>
										<td>{item?.location ? item.location.name : "N/A"}</td>
										<td>{item?.location?.zone?.name ?? "N/A"}</td>
										<td className="at-mono">{item?.digitalAddress || "N/A"}</td>
										<td>
											{item?.rClass?.name ? (
												<span className="at-class">{item.rClass.name}</span>
											) : (
												<span className="ad-muted">N/A</span>
											)}
										</td>
										<td>
											{item.registered ? (
												<span className="at-status at-status--ok">
													<MdCheckCircle /> Registered
												</span>
											) : (
												<span className="at-status">
													<MdWarning /> Not registered
												</span>
											)}
										</td>
										<td>
											<div className="at-actions">
												<button
													className="at-icon-btn"
													title="View"
													aria-label={`View ${item.name}`}
													onClick={() => navigate(`details/${item.slug}`)}
												>
													<MdVisibility />
												</button>
												<button
													className="at-icon-btn at-icon-btn--edit"
													title="Edit"
													aria-label={`Edit ${item.name}`}
													onClick={() => navigate(`${item.slug}/edit`)}
												>
													<MdEdit />
												</button>
												<button
													className="at-icon-btn at-icon-btn--delete"
													title="Delete"
													aria-label={`Delete ${item.name}`}
													onClick={() => setToDelete(item)}
												>
													<MdDelete />
												</button>
											</div>
										</td>
									</tr>
								))}
						</tbody>
					</table>
				</div>

				<div className="at-footer">
					<span className="ad-muted">
						Showing <b>{from}</b>–<b>{to}</b> of <b>{total}</b>
					</span>
					<ReactPaginate
						breakLabel="..."
						nextLabel="Next ›"
						onPageChange={handlePageClick}
						pageCount={pageCount}
						forcePage={page - 1}
						previousLabel="‹ Prev"
						renderOnZeroPageCount={null}
						containerClassName="pagination mb-0"
						pageClassName="page-item"
						pageLinkClassName="page-link"
						activeClassName=" active"
						activeLinkClassName="active"
						breakClassName="page-item"
						breakLinkClassName="page-link"
						nextClassName="page-item"
						nextLinkClassName="page-link"
						previousClassName="page-item"
						previousLinkClassName="page-link"
					/>
				</div>
			</section>

			<Modal show={!!toDelete} onHide={() => !isDeleting && setToDelete(null)} centered>
				<Modal.Body className="at-confirm">
					<span className="at-confirm-icon">
						<MdDelete />
					</span>
					<h4>Delete residence?</h4>
					<p>
						<b>{toDelete?.name}</b> will be permanently removed. This can&apos;t be undone.
					</p>
					<div className="at-confirm-actions">
						<button className="ad-btn ad-btn--ghost" disabled={isDeleting} onClick={() => setToDelete(null)}>
							Cancel
						</button>
						<button className="ad-btn at-btn-danger" disabled={isDeleting} onClick={handleDelete}>
							{isDeleting ? (
								<>
									<span className="ad-spinner" aria-hidden="true" /> Deleting…
								</>
							) : (
								<>
									<MdDelete /> Delete
								</>
							)}
						</button>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
}
